import React, { useState } from 'react';
import { Modal as RBModal } from 'react-bootstrap';
import { Button } from '@rd-web-markets/shared/dist/util/buttons';
import AlertList from '@rd-web-markets/shared/dist/util/AlertList';

/**
 * @param {['xs' | 'sm' | 'md' | 'lg' | xl]} size
 * @param String title
 * @param Function renderBody
 * @param Function renderFooter default = undefined
 * @param Function onHide action to do after cancel/close, before the modal is hidden
 * @param Function onSubmit action to do after submit/save before the modal is hidden
 * @param Boolean show Controls whether the modal is visible
 * @param Boolean disabled Controls whether the submit button is disabled
 * @param {Boolean} removeAllAlerts If true, clears all alerts on submit
 * @param String dialogClassName extra class for the .modal-dialog element
 * @param String dataTestId data-testid for the modal element
 * @
 *
 * Renders a Modal.
 *
 * Use renderBody to pass modal body content.
 *
 * User renderFooter || undefined for footer content. renderFooter receives { submit, loading } so custom footers
 * can reuse the built-in submit handling (loading state, errors shown in the modal, close only on success).
 *
 * onHide is called every time the modal is hidden - by Cancel / Submit / X buttons or clicking outside.
 * Use onHide / onSubmit as modal callbacks for Cancel/Save buttons.
 */
export default function Modal(_ref) {
  let {
    size,
    title,
    renderBody,
    renderFooter,
    renderFooterLeftSide,
    onHide,
    onSubmit,
    show,
    submitIcon,
    submitText,
    closeText,
    closeButtonLoading = false,
    disabled,
    callBackAfterSubmit,
    removeAllAlerts = false,
    centered = true,
    dialogClassName,
    dataTestId
  } = _ref;
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const logError = error => {
    console.error(error);
    const newAlerts = error.userFriendlyMessages ? error.userFriendlyMessages.map((m, index) => ({
      id: error.id + index,
      message: m
    })) : [{
      message: error.message
    }];
    setAlerts([...alerts, ...newAlerts]);
  };
  const handleHide = async () => {
    try {
      setAlerts([]);
      await onHide();
    } catch (error) {
      logError(error);
    }
  };
  const handleSubmit = async e => {
    setAlerts([]);
    setLoading(true);
    e.preventDefault();
    try {
      if (onSubmit) {
        await onSubmit();
        if (callBackAfterSubmit) {
          callBackAfterSubmit();
        } else {
          await handleHide();
        }
      }
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(RBModal, {
    size: size,
    show: show,
    onHide: handleHide,
    centered: centered,
    dialogClassName: dialogClassName,
    "data-testid": dataTestId
  }, title && /*#__PURE__*/React.createElement(RBModal.Header, {
    className: `modal-header-custom-${size}`
  }, /*#__PURE__*/React.createElement(RBModal.Title, {
    className: "text-light"
  }, title)), /*#__PURE__*/React.createElement(RBModal.Body, null, !!alerts.length && /*#__PURE__*/React.createElement("div", {
    className: "mb-3"
  }, /*#__PURE__*/React.createElement(AlertList, {
    alertClassName: "w-100 mw-100",
    alerts: alerts,
    removeAllAlerts: removeAllAlerts,
    setAlerts: setAlerts
  })), renderBody()), /*#__PURE__*/React.createElement(RBModal.Footer, null, renderFooterLeftSide && renderFooterLeftSide(), renderFooter ? renderFooter({
    submit: handleSubmit,
    loading
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    variant: "secondary",
    loading: closeButtonLoading,
    onClick: handleHide
  }, closeText || 'Cancel'), onSubmit && /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    disabled: disabled,
    onClick: handleSubmit,
    loading: loading,
    icon: submitIcon || 'save'
  }, submitText || 'Save')))));
}